import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "@/lib/utils";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  LayoutGrid,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Upload,
  Search,
  Download,
  Info,
  Lightbulb,
  XCircle,
  Check,
  Folder
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Section names mapping
const SECTION_NAMES = {
  hero: "Hero Section",
  offers: "Offers Section",
  about_service: "About the Service",
  benefits_problems: "Benefits/Problems",
  sub_services: "Sub-Services/Types",
  trust_brands: "Trust/Brands (Logos)",
  why_choose: "Why Choose Us",
  usps: "USPs Over Competitors",
  about_company: "About Company",
  cta_bar: "CTA Bar",
  how_it_works: "How It Works",
  gallery: "Our Work (Gallery)",
  testimonials: "Testimonials",
  benefits: "Benefits Section",
  faq: "FAQ Section",
  final_cta: "Final CTA"
};

// Circle Score Meter Component
const ScoreMeter = ({ score, size = 44 }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 75) return { stroke: "#22c55e", text: "text-green-500" };
    if (score >= 50) return { stroke: "#eab308", text: "text-yellow-500" };
    if (score >= 25) return { stroke: "#f97316", text: "text-orange-500" };
    return { stroke: "#ef4444", text: "text-red-500" };
  };
  
  const colors = getColor(score);
  
  return (
    <div className="relative inline-flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#27272a"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn("text-xs font-bold font-mono", colors.text)}>
            {Math.round(score)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Section Badge Component
const SectionBadge = ({ sectionId, present, suggestion, showTooltip = true }) => {
  const [showTip, setShowTip] = useState(false);
  const name = SECTION_NAMES[sectionId] || sectionId;
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div className={cn(
        "flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono border",
        present 
          ? "bg-green-500/10 text-green-500 border-green-500/30"
          : "bg-red-500/10 text-red-400 border-red-500/30"
      )}>
        {present ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        <span className="truncate max-w-[100px]">{name}</span>
      </div>
      
      {/* Suggestion tooltip */}
      {showTooltip && !present && suggestion && showTip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-white font-medium mb-1">{name}</p>
              <p className="text-xs text-zinc-300 leading-relaxed">{suggestion}</p>
            </div>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-zinc-700" />
          </div>
        </div>
      )}
    </div>
  );
};

// Site Card Component
const SiteCard = ({ site, onScan, onDelete, onSelect, onRescanErrors, isSelected, errorCount }) => {
  const getStatusIcon = () => {
    switch (site.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "SCANNING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const progress = site.total_pages > 0 
    ? Math.round((site.scanned_pages / site.total_pages) * 100) 
    : 0;

  return (
    <div 
      onClick={() => onSelect(site)}
      className={cn(
        "bg-zinc-900 border rounded-sm p-4 cursor-pointer transition-all hover:border-zinc-600",
        isSelected ? "border-green-500/50 bg-zinc-900/80" : "border-zinc-800"
      )}
      data-testid={`section-site-card-${site.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-white font-mono text-sm truncate max-w-[180px]" title={site.name}>
            {site.name}
          </span>
        </div>
      </div>
      
      <p className="text-[10px] text-zinc-600 font-mono mb-2 truncate" title={site.domain}>
        {site.domain}
      </p>
      
      {/* Score display */}
      {site.status === "COMPLETED" && (
        <div className="flex items-center justify-between mb-3 bg-zinc-800/50 p-2 rounded-sm">
          <div className="text-center">
            <div className="text-lg font-bold text-white font-mono">{site.avg_sections_present?.toFixed(1) || 0}</div>
            <div className="text-[9px] text-zinc-500">Avg Sections</div>
          </div>
          <ScoreMeter score={site.avg_section_score || 0} size={48} />
        </div>
      )}

      {site.status === "SCANNING" && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-zinc-500 font-mono mb-1">
            <span>Scanning...</span>
            <span>{site.scanned_pages} / {site.total_pages} pages</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {site.last_scan_at && (
        <p className="text-[10px] text-zinc-600 font-mono mb-2">
          Last: {new Date(site.last_scan_at).toLocaleString()}
        </p>
      )}

      <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <SnapshotCompare 
          siteId={site.id} 
          siteName={site.name} 
          monitorType="section"
          buttonClassName="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7 border-zinc-700"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, false)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7"
          title="Crawl new pages only"
        >
          <RefreshCw className={cn("w-3 h-3 mr-1", site.status === "SCANNING" && "animate-spin")} />
          New
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id, true)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm font-mono text-[10px] h-7"
          title="Full rescan all pages"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Full
        </Button>
        {errorCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRescanErrors(site.id)}
            disabled={site.status === "SCANNING"}
            className="flex-1 text-amber-400 hover:text-amber-300 hover:bg-amber-950/30 rounded-sm font-mono text-[10px] h-7"
            title={`Rescan ${errorCount} error pages`}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Retry ({errorCount})
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(site.id)}
          className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-[10px] h-7 px-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Page Row Component with expandable details
const PageRow = ({ page, isExpanded, onToggle }) => {
  const sectionDetails = page.section_details || {};
  const presentSections = page.sections_present || [];
  const missingSections = page.sections_missing || [];
  const prioritySections = page.priority_sections || [];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-zinc-800/50"
        onClick={onToggle}
      >
        <button className="text-zinc-500">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-300 font-mono truncate" title={page.url}>
            {page.url.replace(/^https?:\/\/[^/]+/, '')}
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1 text-xs font-mono">
            <Check className="w-3 h-3 text-green-500" />
            <span className="text-green-500">{presentSections.length}</span>
            <span className="text-zinc-600">/</span>
            <XCircle className="w-3 h-3 text-red-500" />
            <span className="text-red-500">{missingSections.length}</span>
          </div>
          
          <ScoreMeter score={page.section_score || 0} size={40} />
        </div>
        
        <a 
          href={page.url} 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-zinc-500 hover:text-white p-1"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      
      {isExpanded && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950/50">
          {/* Priority Sections to Add */}
          {prioritySections.length > 0 && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <h4 className="text-xs text-amber-400 font-mono mb-2 flex items-center gap-1">
                <Lightbulb className="w-4 h-4" />
                Priority Sections to Add
              </h4>
              <div className="flex flex-wrap gap-2">
                {prioritySections.map((sectionId) => (
                  <span key={sectionId} className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs font-mono rounded">
                    {SECTION_NAMES[sectionId] || sectionId}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Overall Suggestion */}
          {page.overall_suggestion && (
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
              <h4 className="text-xs text-blue-400 font-mono mb-2 flex items-center gap-1">
                <Info className="w-4 h-4" />
                Overall Recommendation
              </h4>
              <p className="text-xs text-zinc-300 leading-relaxed">{page.overall_suggestion}</p>
            </div>
          )}
          
          {/* Sections Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Present Sections */}
            <div>
              <h4 className="text-xs text-green-400 font-mono mb-2 flex items-center gap-1">
                <Check className="w-4 h-4" />
                Present Sections ({presentSections.length})
              </h4>
              <div className="flex flex-wrap gap-1">
                {presentSections.map((sectionId) => (
                  <SectionBadge 
                    key={sectionId} 
                    sectionId={sectionId} 
                    present={true}
                    showTooltip={false}
                  />
                ))}
                {presentSections.length === 0 && (
                  <p className="text-xs text-zinc-500 italic">No sections detected</p>
                )}
              </div>
            </div>
            
            {/* Missing Sections with Suggestions */}
            <div>
              <h4 className="text-xs text-red-400 font-mono mb-2 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                Missing Sections ({missingSections.length})
              </h4>
              <div className="flex flex-wrap gap-1">
                {missingSections.map((sectionId) => (
                  <SectionBadge 
                    key={sectionId} 
                    sectionId={sectionId} 
                    present={false}
                    suggestion={sectionDetails[sectionId]?.suggestion}
                  />
                ))}
                {missingSections.length === 0 && (
                  <p className="text-xs text-zinc-500 italic">All sections present!</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Detailed Section Breakdown */}
          <div className="mt-4">
            <h4 className="text-xs text-zinc-400 font-mono mb-2">Section Details (hover for suggestions)</h4>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(sectionDetails).map(([sectionId, data]) => (
                <div 
                  key={sectionId}
                  className={cn(
                    "p-2 rounded-sm text-xs font-mono border",
                    data.present 
                      ? "bg-green-500/5 border-green-500/20"
                      : "bg-red-500/5 border-red-500/20"
                  )}
                  title={data.suggestion || ""}
                >
                  <div className="flex items-center justify-between">
                    <span className={cn(
                      "truncate",
                      data.present ? "text-green-400" : "text-red-400"
                    )}>
                      {SECTION_NAMES[sectionId]?.split(' ')[0] || sectionId}
                    </span>
                    <span className="text-zinc-500">{data.confidence || 0}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Section Coverage Bar
const CoverageBar = ({ sectionId, coverage, name }) => {
  const getColor = (coverage) => {
    if (coverage >= 75) return "bg-green-500";
    if (coverage >= 50) return "bg-yellow-500";
    if (coverage >= 25) return "bg-orange-500";
    return "bg-red-500";
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-zinc-400 font-mono w-24 truncate" title={name}>
        {name}
      </span>
      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={cn("h-full transition-all duration-300", getColor(coverage))}
          style={{ width: `${coverage}%` }}
        />
      </div>
      <span className="text-[10px] text-zinc-400 font-mono w-10 text-right">{coverage}%</span>
    </div>
  );
};

// Main Section Monitor Component
export const SectionMonitor = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteErrorCounts, setSiteErrorCounts] = useState({});
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageFilter, setPageFilter] = useState("all");
  const [selectedSitemapType, setSelectedSitemapType] = useState("all");
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredSites = sites.filter((site) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.domain?.toLowerCase().includes(query)
    );
  });

  const fetchErrorCounts = useCallback(async (siteList) => {
    const counts = {};
    for (const site of siteList) {
      if (site.status === 'COMPLETED') {
        try {
          const response = await axios.get(`${API}/section-monitor/${site.id}/stats`);
          counts[site.id] = response.data.errors || 0;
        } catch (e) {
          counts[site.id] = 0;
        }
      }
    }
    setSiteErrorCounts(counts);
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/section-monitor`);
      setSites(response.data);
      fetchErrorCounts(response.data);
      
      if (response.data.length > 0 && !selectedSite) {
        setSelectedSite(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, fetchErrorCounts]);

  const fetchStats = useCallback(async () => {
    if (!selectedSite) return;
    
    try {
      const response = await axios.get(`${API}/section-monitor/${selectedSite.id}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [selectedSite]);

  const fetchPages = useCallback(async () => {
    if (!selectedSite) return;
    
    setIsLoadingPages(true);
    try {
      const params = new URLSearchParams();
      if (pageFilter !== "all") params.append('filter', pageFilter);
      if (selectedSitemapType && selectedSitemapType !== "all") params.append('sitemap_type', selectedSitemapType);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/section-monitor/${selectedSite.id}/pages${queryString}`);
      setPages(response.data);
    } catch (e) {
      console.error("Failed to fetch pages:", e);
    } finally {
      setIsLoadingPages(false);
    }
  }, [selectedSite, pageFilter, selectedSitemapType]);

  useEffect(() => {
    fetchSites();
    fetchGroups();
    const interval = setInterval(fetchSites, 10000);
    return () => clearInterval(interval);
  }, [fetchSites, fetchGroups]);

  // Reset sitemap type filter only when site changes
  useEffect(() => {
    if (selectedSite) {
      setSelectedSitemapType("all");
    }
  }, [selectedSite?.id]);

  // Fetch stats and pages when site, filter, or sitemap type changes
  useEffect(() => {
    if (selectedSite) {
      fetchStats();
      fetchPages();
    }
  }, [selectedSite, pageFilter, selectedSitemapType, fetchStats, fetchPages]);

  const handleAddSite = async () => {
    if (!sitemapUrl) {
      toast.error("Please enter a sitemap URL");
      return;
    }

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/section-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName,
        groups: selectedGroups
      });
      toast.success("Site added - scanning started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      fetchSites();
      setSelectedSite(response.data);
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add site";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleScan = async (siteId, full = false) => {
    try {
      await axios.post(`${API}/section-monitor/${siteId}/scan?full=${full}`);
      toast.success(full ? "Full scan started" : "Scan started");
      fetchSites();
    } catch (e) {
      toast.error("Failed to start scan");
    }
  };

  const handleRescanErrors = async (siteId) => {
    try {
      const response = await axios.post(`${API}/section-monitor/${siteId}/rescan-errors`);
      if (response.data.error_count > 0) {
        toast.success(`Rescanning ${response.data.error_count} error pages`);
      } else {
        toast.info("No error pages to rescan");
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to start rescan");
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm("Delete this site and all its data?")) return;
    
    try {
      await axios.delete(`${API}/section-monitor/${siteId}`);
      toast.success("Site deleted");
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
        setPages([]);
        setStats(null);
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to delete site");
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/section-monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      
      if (added.length > 0) toast.success(`Imported ${added.length} site(s)`);
      if (skipped.length > 0) toast.info(`Skipped ${skipped.length} duplicate(s)`);
      if (errors.length > 0) toast.error(`${errors.length} URL(s) had errors`);
      
      fetchSites();
    } catch (e) {
      toast.error("Failed to import CSV");
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const handleExportCsv = () => {
    if (!selectedSite) return;
    const filterParam = pageFilter !== "all" ? `?filter=${pageFilter}` : "";
    window.open(`${API}/section-monitor/${selectedSite.id}/export-csv${filterParam}`, '_blank');
    toast.success("Export started");
  };

  const filters = [
    { id: "all", label: "All Pages" },
    { id: "excellent", label: "Excellent (75%+)" },
    { id: "good", label: "Good (50-74%)" },
    { id: "fair", label: "Fair (25-49%)" },
    { id: "poor", label: "Poor (<25%)" },
    { id: "errors", label: "Errors" },
  ];

  return (
    <div className="p-6" data-testid="section-monitor">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-2">
          <LayoutGrid className="w-6 h-6 text-green-500" />
          Section Monitoring
        </h1>
        <p className="text-zinc-500 text-sm font-mono mt-1">
          Analyze pages for 16 common sections with AI-powered suggestions
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Sites List */}
        <div className="col-span-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <h2 className="text-sm font-mono text-white mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-green-500" />
              MONITORED SITES
            </h2>

            {/* Add Site Form */}
            <div className="space-y-2 mb-4">
              <Input
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                data-testid="section-site-name-input"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="section-sitemap-input"
                />
                <Button
                  onClick={handleAddSite}
                  disabled={isAdding || !sitemapUrl}
                  className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs h-8 px-3"
                  data-testid="section-add-btn"
                >
                  {isAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Group Selection */}
              {groups.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-zinc-500 font-mono">Groups:</span>
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroupSelection(group.id)}
                      className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors",
                        selectedGroups.includes(group.id)
                          ? "bg-zinc-700 text-white"
                          : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                      )}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Import CSV */}
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvImport}
                    className="hidden"
                    data-testid="section-csv-input"
                  />
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-mono text-xs h-8"
                    disabled={isImporting}
                    asChild
                  >
                    <span>
                      <Upload className="w-3 h-3 mr-1" />
                      {isImporting ? "Importing..." : "Import CSV"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="section-search-input"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-sm px-2 py-1 text-[10px] text-zinc-300 font-mono h-8"
                  data-testid="section-group-filter"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Sites List */}
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredSites.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                <LayoutGrid className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sites yet</p>
                <p className="text-xs mt-1">Add a sitemap URL to start</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onScan={handleScan}
                    onDelete={handleDelete}
                    onSelect={setSelectedSite}
                    onRescanErrors={handleRescanErrors}
                    isSelected={selectedSite?.id === site.id}
                    errorCount={siteErrorCounts[site.id] || 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Site Details */}
        <div className="col-span-8">
          {selectedSite ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-mono text-white flex items-center gap-2">
                  <LayoutGrid className="w-4 h-4 text-green-500" />
                  {selectedSite.name}
                </h2>
                <span className="text-xs text-zinc-500 font-mono">
                  {selectedSite.total_pages} pages
                </span>
              </div>

              {/* Stats & Section Coverage */}
              {stats && (
                <div className="grid grid-cols-12 gap-4 mb-4">
                  {/* Score Distribution */}
                  <div className="col-span-4 bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-2">SCORE DISTRIBUTION</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-green-500">Excellent (75%+)</span>
                        <span className="text-white">{stats.excellent}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-yellow-500">Good (50-74%)</span>
                        <span className="text-white">{stats.good}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-orange-500">Fair (25-49%)</span>
                        <span className="text-white">{stats.fair}</span>
                      </div>
                      <div className="flex justify-between text-xs font-mono">
                        <span className="text-red-500">Poor (&lt;25%)</span>
                        <span className="text-white">{stats.poor}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Section Coverage */}
                  <div className="col-span-8 bg-zinc-800/50 border border-zinc-700 rounded-sm p-3">
                    <div className="text-[10px] text-zinc-500 font-mono mb-2">SECTION COVERAGE (% of pages with section)</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-32 overflow-y-auto">
                      {stats.section_coverage && Object.entries(stats.section_coverage).map(([sectionId, coverage]) => (
                        <CoverageBar 
                          key={sectionId}
                          sectionId={sectionId}
                          coverage={coverage}
                          name={SECTION_NAMES[sectionId] || sectionId}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Filter Tabs */}
              <div className="flex gap-1 mb-4 overflow-x-auto items-center">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setPageFilter(f.id)}
                    className={cn(
                      "px-3 py-1.5 font-mono text-xs rounded-sm transition-colors whitespace-nowrap",
                      pageFilter === f.id 
                        ? "bg-zinc-700 text-white" 
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                    )}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="flex-1" />
                {/* Sitemap Type Filter */}
                {selectedSite?.sitemap_types && selectedSite.sitemap_types.length > 1 && (
                  <select
                    value={selectedSitemapType}
                    onChange={(e) => setSelectedSitemapType(e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 rounded-sm text-xs font-mono px-2 py-1 text-zinc-300 focus:outline-none focus:border-zinc-500 mr-2"
                    data-testid="sitemap-type-filter"
                  >
                    <option value="all">All Types</option>
                    {selectedSite.sitemap_types.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCsv}
                  disabled={pages.length === 0}
                  className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-xs h-7"
                  data-testid="export-section-csv-btn"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Export CSV
                </Button>
              </div>

              {/* Pages List */}
              <div className="space-y-2 max-h-[calc(100vh-450px)] overflow-y-auto">
                {isLoadingPages ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                      <div className="h-4 bg-zinc-800 rounded w-3/4" />
                    </div>
                  ))
                ) : pages.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                    <p>No pages in this category</p>
                  </div>
                ) : (
                  pages.map((page) => (
                    <PageRow
                      key={page.id}
                      page={page}
                      isExpanded={expandedPage === page.id}
                      onToggle={() => setExpandedPage(expandedPage === page.id ? null : page.id)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center text-zinc-500 font-mono">
                <LayoutGrid className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">Select a site to view details</p>
                <p className="text-xs mt-1">or add a new sitemap URL</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionMonitor;
